<script lang="ts">
	import { format } from 'svelte-i18n';
	import { requestExitEditMode } from 'src/helpers/edit_chrome_helpers';
	import {
		ButtonV2,
		Container,
		DropdownMenu,
		IconButton,
		MenuItemList,
		Modal,
		ToggleV2,
		Text,
	} from '@Shelly/shelly-design-system';
	import TabsActionsButton from 'src/components/TabsActionsButton.svelte';
	import { edit } from 'src/stores/ui/edit_lists';
	import { ENERGY_MONITORING_PERIODS, type EnergyMonitoringPeriodId } from './helpers/period_helpers';
	import {
		energyMonitoringDataMode,
		energyMonitoringPeriodId,
		setEnergyMonitoringDataMode,
		setEnergyMonitoringPeriod,
	} from './helpers/store';
	import type { MenuItemListExtendedProps } from 'src/components/Dashboards/dash-types';
	import { energyV2Actions } from './v2/energy-v2-ui';
	import { ENERGY_V2_PRESETS, type EnergyPurposeId } from './v2/energy-v2-types';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { user_data } from 'src/stores/account/user';

	export let manageDashboardMenuItems: MenuItemListExtendedProps[] = [];

	let showDropdown = false;
	let cloning = false;
	let restoring = false;
	let showRestoreModal = false;
	let switchingPurpose = false;

	$: selectedPeriod = $energyMonitoringPeriodId;
	$: useDemoData = $energyMonitoringDataMode === 'demo';
	$: isEnergyDashboard = Boolean($energyV2Actions);
	$: showPresetSwitcher = isEnergyDashboard && isTestUser() && Boolean($user_data);
	$: activePurpose = $energyV2Actions?.activePurpose ?? 'home';

	$: selectOptions = ENERGY_MONITORING_PERIODS.map((option) => ({
		title: $format(option.labelKey, { default: option.labelDefault }),
		value: option.id,
	}));

	const onPeriodChange = (periodId: EnergyMonitoringPeriodId) => {
		if (!periodId || periodId === selectedPeriod) return;
		setEnergyMonitoringPeriod(periodId);
	};

	const onDataModeToggle = () => {
		setEnergyMonitoringDataMode(useDemoData ? 'real' : 'demo');
	};

	const stopMenuEvents = (node: HTMLElement) => {
		const stopPropagation = (event: Event) => event.stopPropagation();
		node.addEventListener('click', stopPropagation);
		return {
			destroy: () => node.removeEventListener('click', stopPropagation),
		};
	};

	const onEditRows = () => {
		$energyV2Actions?.onToggleEdit();
		showDropdown = false;
	};

	const onClone = async () => {
		if (!$energyV2Actions?.onClone) return;
		cloning = true;
		showDropdown = false;
		try {
			await $energyV2Actions.onClone();
		} finally {
			cloning = false;
		}
	};

	const onRestore = async () => {
		if (!$energyV2Actions?.onRestore) return;
		restoring = true;
		try {
			await $energyV2Actions.onRestore();
			showRestoreModal = false;
		} finally {
			restoring = false;
		}
	};

	const onSwitchPurpose = async (purpose: EnergyPurposeId) => {
		if (!$energyV2Actions || switchingPurpose) return;
		if (purpose === $energyV2Actions.activePurpose) return;
		switchingPurpose = true;
		try {
			await $energyV2Actions.onPurposeSave(purpose);
		} finally {
			switchingPurpose = false;
		}
	};

	const exitEdit = () => requestExitEditMode();

	const onManageItemClick = (item: MenuItemListExtendedProps) => {
		showDropdown = false;
		item.onClick?.(item);
	};
</script>

{#if $edit && isEnergyDashboard}
	<ButtonV2
		size="m"
		variant="primary"
		width="full"
		icon="fas fa-xmark"
		aria-label={$format('button_exit_edit', { default: 'Exit edit mode' })}
		on:click={exitEdit}
	>
		{$format('button_exit_edit', { default: 'Exit edit mode' })}
	</ButtonV2>
{:else}
	<DropdownMenu bind:show={showDropdown}>
		<TabsActionsButton
			slot="trigger"
			variant="help-2"
			isActive={showDropdown}
			ariaLabel={$format('aria_energy_actions', { default: 'Energy dashboard actions' })}
			on:click={() => (showDropdown = !showDropdown)}
		/>

		{#if $energyV2Actions && showPresetSwitcher}
			<div class="energy-dash-actions-presets-testing">
				<Text styleType="miniature" class="energy-dash-actions-presets-testing-label">
					for testing
				</Text>
				<MenuItemList
					isGroupTitle
					size="m"
					title={$format('energy_dashboard_switch_preset', { default: 'Switch Preset' })}
				/>
				<div class="energy-dash-actions-presets" use:stopMenuEvents>
					<div class="energy-dash-actions-preset-icons">
						{#each ENERGY_V2_PRESETS as preset (preset.id)}
							<IconButton
								size="m"
								icon={preset.icon}
								variant={activePurpose === preset.id ? 'primary' : 'secondary'}
								isActive={activePurpose === preset.id}
								disabled={switchingPurpose}
								aria-label={$format(preset.labelKey, { default: preset.labelDefault })}
								title={`${$format(preset.labelKey, {
									default: preset.labelDefault,
								})}: ${$format(preset.descriptionKey, {
									default: preset.descriptionDefault,
								})}`}
								on:click={() => onSwitchPurpose(preset.id)}
							/>
						{/each}
					</div>
				</div>
			</div>
		{/if}

		<MenuItemList isGroupTitle size="m" title={$format('label_period', { default: 'Period' })} />
		{#each selectOptions as option (option.value)}
			<MenuItemList
				isGroupItem
				isSelectable
				size="m"
				icon="fas fa-clock"
				title={option.title}
				selected={option.value === selectedPeriod}
				on:click={() => onPeriodChange(option.value)}
			/>
		{/each}

		<MenuItemList
			isGroupTitle
			size="m"
			title={$format('dropdown_group_data_source', { default: 'Data source' })}
		/>
		<div class="energy-dash-actions-data-mode" use:stopMenuEvents>
			<div class="energy-dash-actions-data-mode-row">
				<Text styleType="body-small" class="text-600">
					{$format('energy_monitoring_data_mode_demo', { default: 'Demo data' })}
				</Text>
				<ToggleV2
					id="em-data-mode-toggle"
					size="m"
					checked={useDemoData}
					on:click={onDataModeToggle}
					aria-label={$format('energy_monitoring_data_mode_toggle', {
						default: 'Toggle between real and demo energy data',
					})}
				/>
			</div>
			<Text styleType="miniature" class="text-color-muted">
				{useDemoData
					? $format('energy_monitoring_data_mode_demo_hint', {
							default: 'Showing sample data to preview the dashboard.',
						})
					: $format('energy_monitoring_data_mode_real_hint', {
							default: 'Using live account meters and statistics.',
						})}
			</Text>
		</div>

		{#if isEnergyDashboard}
			<MenuItemList
				isGroupTitle
				size="m"
				title={$format('dropdown_group_dashboard', { default: 'Dashboard' })}
			/>
			<MenuItemList
				isGroupItem
				size="m"
				title={$format('label_edit_widgets', { default: 'Edit widgets' })}
				icon="fas fa-pen-to-square"
				on:click={onEditRows}
			/>
			<MenuItemList
				isGroupItem
				size="m"
				title={$format('energy_dashboard_clone', { default: 'Clone current setup' })}
				icon="fas fa-clone"
				disabled={cloning}
				on:click={onClone}
			/>
			<MenuItemList
				isGroupItem
				size="m"
				title={$format('energy_dashboard_restore', { default: 'Restore to default' })}
				icon="fas fa-arrow-rotate-left"
				disabled={restoring}
				on:click={() => {
					showDropdown = false;
					showRestoreModal = true;
				}}
			/>
		{/if}

		{#each manageDashboardMenuItems as groupItem, groupIndex}
			{#if groupItem.title}
				<MenuItemList isGroupTitle={groupItem.isGroupTitle} size="m" title={groupItem.title} />
			{:else if groupIndex > 0}
				<div class="energy-dash-actions-divider"></div>
			{/if}
			{#each groupItem.items || [] as menuListItem}
				<MenuItemList
					isGroupItem
					size="m"
					title={menuListItem.title}
					titleClasses={menuListItem.titleClasses}
					icon={menuListItem.icon}
					iconStyle={menuListItem?.iconStyle}
					disabled={menuListItem.disabled}
					on:click={() => onManageItemClick(menuListItem)}
				/>
			{/each}
		{/each}
	</DropdownMenu>
{/if}

<Modal
	showModal={showRestoreModal}
	title={$format('energy_dashboard_restore_title', { default: 'Restore this preset?' })}
	on:close={() => (showRestoreModal = false)}
>
	<Container slot="content" padding="0">
		<Text>
			{$format('energy_dashboard_restore_message', {
				default:
					'This replaces the widgets, settings, and column order for the active preset. Other presets will stay unchanged.',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={() => (showRestoreModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={restoring}
			aria-label={$format('energy_dashboard_restore', { default: 'Restore to default' })}
			on:click={onRestore}
		>
			{$format('energy_dashboard_restore', { default: 'Restore to default' })}
		</ButtonV2>
	</div>
</Modal>

<style>
	.energy-dash-actions-presets-testing {
		width: 100%;
		margin: 0;
		padding-top: var(--spacing-xs);
		border: 1px solid rgba(234, 179, 8, 0.75);
		border-radius: 0;
		background: rgba(234, 179, 8, 0.1);
		box-sizing: border-box;
	}

	.energy-dash-actions-presets-testing-label {
		display: block;
		padding: 0 var(--spacing-s);
		font-size: 9px;
		line-height: 1;
		letter-spacing: 0.04em;
		text-transform: lowercase;
		color: rgb(161, 98, 7);
	}

	.energy-dash-actions-presets {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		padding: 0 var(--spacing-s) var(--spacing-s);
	}

	.energy-dash-actions-preset-icons {
		display: flex;
		gap: var(--spacing-xs);
	}

	.energy-dash-actions-data-mode {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		padding: 0 var(--spacing-s) var(--spacing-s);
		min-width: 220px;
	}

	.energy-dash-actions-data-mode-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-s);
	}

	.energy-dash-actions-divider {
		border-top: 1px solid var(--color-3);
	}
</style>
